/**
 * Care bloc poarta titlul paginii.
 *
 * Regula sta INTR-UN MODUL PUR, separat de randare, din doua motive: e singura
 * care decide daca documentul are `h1` (deci merita plasa de teste), iar
 * harnessul de teste al proiectului include din `web` doar fisierele fara React.
 */

import { blockText, type MarketBlockType, type MarketPageBlock } from "@deliveryapp/shared";

/**
 * Blocurile care afiseaza SIGUR un titlu, indiferent de configurare si de date:
 * au o rezerva negoala pentru titlu SI nu se pot ascunde singure la randare.
 */
const ALWAYS_TITLED: ReadonlySet<MarketBlockType> = new Set<MarketBlockType>([
  "hero-search",
  "reservations",
]);

/**
 * Blocurile care afiseaza sigur un titlu ATUNCI CAND operatorul a scris unul.
 *
 * Lista e mai scurta decat „toate blocurile cu camp de titlu", si diferenta e
 * exact clasa de defect: `steps`, `faq`, `gallery`, `stats`, `testimonials` si
 * `features` se ascund complet cand lista lor de elemente e goala, iar
 * `menu-highlights`, `coverage-list`, `hours` si `contact` se ascund cand nu au
 * ce arata (meniu gol, nicio zona activa, program neconfigurat). Un titlu scris
 * pe ele NU garanteaza ca ceva ajunge pe ecran.
 */
const TITLED_WHEN_FILLED: ReadonlySet<MarketBlockType> = new Set<MarketBlockType>([
  "hero",
  "text",
  "image-text",
  "cta-banner",
]);

/**
 * Blocul asta chiar va afisa un titlu de sectiune?
 *
 * Intrebarea se pune pe ce AJUNGE PE ECRAN, nu pe tipul blocului. Doua moduri de
 * esec, ambele terminand cu un document fara niciun `h1`:
 *  - o coperta cu imagine si fara titlu (alegere de design obisnuita) consuma
 *    nivelul 1 si nu randeaza nimic;
 *  - un „Din meniu" cu titlu, pus primul, dispare in ziua in care meniul nu se
 *    poate citi — iar titlul paginii dispare odata cu el.
 * In ambele cazuri raspunsul de aici e `false`, deci pagina cade pe titlul ei de
 * rezerva.
 */
export function carriesHeading(block: MarketPageBlock, storefrontCount: number): boolean {
  // `storefront-grid` isi ascunde titlul cand exista o singura vitrina: acolo nu
  // e o multime de etichetat, iar „Restaurante" deasupra unui singur card ar fi
  // un titlu pentru nimic. De aceea nu mai e in `ALWAYS_TITLED`, si de aceea
  // raspunsul nu se poate da doar din bloc — depinde de cate vitrine are piata.
  //
  // Fara ramura asta, stiva ar fi crezut ca blocul poarta titlul paginii, nu ar
  // mai fi randat titlul de rezerva, iar pagina de start a unei piete cu un
  // singur restaurant ar fi ramas complet fara `h1`.
  // `!== 1`, nu `> 1`: cu ZERO vitrine sectiunea isi pastreaza antetul (piata nu
  // poate primi comenzi, iar mesajul are nevoie de titlu ca sa fie gasibil prin
  // ancora din copertă). Regula trebuie sa spuna exact ce randeaza blocul —
  // orice abatere inseamna ori doua titluri, ori niciunul.
  if (block.type === "storefront-grid") return storefrontCount !== 1;
  if (ALWAYS_TITLED.has(block.type)) return true;
  if (!TITLED_WHEN_FILLED.has(block.type)) return false;
  return blockText(block, "title").length > 0;
}
